/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/bluemateInput.scss';

import React, {
    ReactNode,
    ChangeEventHandler,
    CSSProperties,
    DetailedHTMLProps,
    FocusEventHandler,
    InputHTMLAttributes,
    KeyboardEventHandler,
    MouseEventHandler,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {AwesomeIcon, ElementPosition, ErrorType, OrnamentShape, stopPropagation} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import {useTranslation} from 'react-i18next';
import {createPortal} from 'react-dom';
import {COLOR_BLUE_BLUEMATE} from '../../../util/constants';
import IDate from '@dvrd/idate';
import DatePickerPopup from '../date/datePickerPopup';
import {BluemateControlShape, BluemateControlSize, DateInputConfig} from '../../../util/types';

interface Props {
    onChange: ChangeEventHandler;
    onFocus: FocusEventHandler;
    onBlur: FocusEventHandler;
    onKeyDown: KeyboardEventHandler;
    value: string | number;
    disabled?: boolean;
    autoFocus?: boolean;
    autoSelect?: boolean;
    label?: ReactNode;
    ornaments?: OrnamentShape | OrnamentShape[];
    inputProps?: Omit<
        DetailedHTMLProps<
            InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
            HTMLInputElement | HTMLTextAreaElement
        >,
        'crossOrigin'
    >;
    className?: string;
    inputClassName?: string;
    labelClassName?: string;
    ornamentClassName?: string;
    errorClassName?: string;
    error?: ErrorType;
    id: string;
    fullWidth?: boolean;
    active?: boolean;
    area?: boolean;
    placeholder?: string;
    style?: CSSProperties;
    size: BluemateControlSize;
    shape: BluemateControlShape;
    styled?: boolean;
    /** Unified form typography: 0.9rem/500 label, 1rem/500 input text — so
     * mixed controls in one form read as one family. Defaults to false. */
    uniformFont?: boolean;
    /** Shows a clear (×) button on the right while the field holds a value. */
    clearable?: boolean;
    onClear?: MouseEventHandler;
    /** Turns the field into a date field: read-only text plus the shared
     *  calendar popup. `value` stays the source of truth (ISO or `format`),
     *  `date.onChange` hands the picked day back as an IDate. */
    date?: DateInputConfig | null;
}

const DEFAULT_DATE_FORMAT = 'DD-MM-YYYY';

export default function BluemateInput(props: Props) {
    const {
        ornaments,
        autoFocus,
        onFocus,
        autoSelect,
        inputProps,
        inputClassName,
        errorClassName,
        className,
        id,
        area,
        onChange,
        onKeyDown,
        disabled,
        onBlur,
        fullWidth,
        labelClassName,
        ornamentClassName,
        label,
        value,
        error,
        placeholder,
        style,
        size,
        shape,
        styled,
        uniformFont,
        clearable,
        onClear,
        date
    } = props;
    const {t} = useTranslation();
    const [active, setActive] = useState(autoFocus === true);
    const [pickerOpen, setPickerOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
    const hasError = useMemo(() => !!error, [error]);

    function onClickElement() {
        if (date && !area) {
            if (!disabled) setPickerOpen(true);
            return;
        }
        focusInput();
    }

    function onClickClear(evt: React.MouseEvent) {
        evt.stopPropagation();
        onClear?.(evt);
        focusInput();
    }

    function onClosePicker() {
        setPickerOpen(false);
    }

    function onPickDate(daySelected: boolean) {
        return function (picked: IDate) {
            if (!date) return;
            if (date.min && picked.isBefore(date.min, 'day')) return;
            if (date.max && picked.isAfter(date.max, 'day')) return;
            date.onChange(picked.clone());
            if (daySelected && date.closeOnChange !== false) onClosePicker();
        };
    }

    function getDateValue(): IDate | null {
        if (!date) return null;
        const raw = typeof value === 'number' ? String(value) : (value ?? '');
        if (!raw.length) return null;
        const parsed = new IDate(raw);
        return parsed.isValid() ? parsed : null;
    }

    function onFocusInput(evt: React.FocusEvent) {
        if (!disabled) {
            setActive(true);
            if (autoSelect) {
                const element = inputRef.current;
                if (element) {
                    element.selectionStart = 0;
                    element.selectionEnd = value.toString().length;
                }
            }
        }
        onFocus(evt);
    }

    function onBlurInput(evt: React.FocusEvent) {
        if (!disabled) setActive(false);
        onBlur(evt);
    }

    function focusInput() {
        inputRef.current?.focus();
    }

    function showClear(): boolean {
        if (!clearable || disabled) return false;
        return String(value ?? '').length > 0;
    }

    function getContentStyle(): CSSProperties {
        const style: CSSProperties = {gridTemplateColumns: '1fr'};
        if (props.active) style.border = `2px solid ${COLOR_BLUE_BLUEMATE}`;
        if (!ornaments || (Array.isArray(ornaments) && !ornaments.length)) style.gridTemplateColumns = '1fr';
        let hasLeft: boolean = false,
            hasRight: boolean = false;
        if (Array.isArray(ornaments))
            for (const ornament of ornaments) {
                if (!ornament.placement || ornament.placement === ElementPosition.LEFT) hasLeft = true;
                else if (ornament.placement === ElementPosition.RIGHT) hasRight = true;
                if (hasLeft && hasRight) break;
            }
        else if (ornaments) {
            hasLeft = !ornaments.placement || ornaments.placement === ElementPosition.LEFT;
            hasRight = ornaments.placement === ElementPosition.RIGHT;
        }
        let columns: string = '1fr';
        let paddingLeft: string = '';
        let paddingRight: string = '';
        if (hasRight) {
            columns = `${columns} fit-content(100%)`;
            paddingRight = '.75rem';
        }
        if (showClear()) {
            columns = `${columns} fit-content(100%)`;
            paddingRight = '.75rem';
        }
        if (date && !area) {
            columns = `${columns} auto`;
            paddingRight = '';
        }
        if (hasLeft) {
            columns = `fit-content(100%) ${columns}`;
            paddingLeft = '.75rem';
        }
        style.gridTemplateColumns = columns;
        style.paddingRight = paddingRight;
        style.paddingLeft = paddingLeft;
        return style;
    }

    function renderLabel() {
        if (!label) return null;
        return (
            <label className={classNames('bluemate-input-label', labelClassName)} onClick={onClickElement}>
                {label}
            </label>
        );
    }

    function renderInput() {
        const dateValue = getDateValue();
        const props = {
            value: date ? (dateValue ? dateValue.format(date.format ?? DEFAULT_DATE_FORMAT) : '') : value,
            onChange,
            onFocus: onFocusInput,
            onBlur: onBlurInput,
            onKeyDown,
            disabled,
            className: classNames('bluemate-input', inputClassName),
            id: `${id}-input`,
            placeholder,
            ...inputProps,
            ...(date ? {readOnly: true} : {})
        };
        if (area) return <textarea {...props} ref={inputRef} />;
        return <input {...props} ref={inputRef} />;
    }

    function renderLeftOrnament() {
        if (!ornaments || (Array.isArray(ornaments) && !ornaments.length)) return null;
        if (Array.isArray(ornaments)) {
            for (const ornament of ornaments) {
                if (!ornament.placement || ornament.placement === ElementPosition.LEFT) return renderOrnament(ornament);
            }
        } else if (!ornaments.placement || ornaments.placement === ElementPosition.LEFT)
            return renderOrnament(ornaments);
        return null;
    }

    function renderRightOrnament() {
        if (!ornaments || (Array.isArray(ornaments) && !ornaments.length)) return null;
        if (Array.isArray(ornaments)) {
            for (const ornament of ornaments) {
                if (ornament.placement === ElementPosition.RIGHT) return renderOrnament(ornament);
            }
        } else if (ornaments.placement === ElementPosition.RIGHT) return renderOrnament(ornaments);
        return null;
    }

    function renderOrnament(ornament: OrnamentShape) {
        if (typeof ornament.element === 'string')
            return (
                <span className={classNames('bluemate-input-ornament', ornamentClassName)} onClick={onClickElement}>
                    {ornament.element}
                </span>
            );
        else
            return React.cloneElement(ornament.element, {
                className: classNames(ornament.element.props.className, ornamentClassName, 'bluemate-input-ornament'),
                onClick: (evt: React.MouseEvent) => {
                    onClickElement();
                    if (React.isValidElement(ornament.element)) {
                        const element: React.ReactElement<any> = ornament.element;
                        element.props.onClick?.(evt);
                    }
                }
            });
    }

    function renderClear() {
        if (!showClear()) return null;
        return (
            <button
                type='button'
                className='bluemate-input-clear'
                aria-label={t('controls.input.clear', 'Wissen')}
                onClick={onClickClear}
            >
                <AwesomeIcon name='times' />
            </button>
        );
    }

    function renderCalendarIcon() {
        if (!date || area) return null;
        return (
            <div className='bluemate-input-calendar'>
                <AwesomeIcon name='calendar-alt' />
            </div>
        );
    }

    function renderError() {
        return <span className={classNames('bluemate-input-error', errorClassName)}>{error}</span>;
    }

    useEffect(() => {
        if (!disabled && autoFocus) focusInput();
    }, [autoFocus, disabled]);

    return (
        <div
            onClick={onClickElement}
            id={id}
            className={classNames(
                'bluemate-input-container',
                disabled && 'disabled',
                hasError && 'error',
                active && 'active',
                fullWidth && 'full',
                `size-${size}`,
                `shape-${shape}`,
                styled && 'styled',
                uniformFont && 'uniform-font',
                !!date && !area && 'date',
                className
            )}
            style={style}
        >
            {renderLabel()}
            <div className='content-container' style={getContentStyle()}>
                {renderLeftOrnament()}
                {renderInput()}
                {renderRightOrnament()}
                {renderClear()}
                {renderCalendarIcon()}
            </div>
            {!!date &&
                !area &&
                pickerOpen &&
                createPortal(
                    // React portals bubble through the component tree, so a click
                    // inside the popup would reach the field and reopen it — the
                    // Close/Today buttons looked dead because of that.
                    <div onClick={stopPropagation} onMouseDown={stopPropagation}>
                        <DatePickerPopup
                            onChange={onPickDate}
                            onClose={onClosePicker}
                            open={pickerOpen}
                            value={getDateValue()}
                            min={date.min}
                            max={date.max}
                            allowBodyScroll={date.allowBodyScroll}
                        />
                    </div>,
                    document.body
                )}
            {renderError()}
        </div>
    );
}
